import React, { useState, useRef } from 'react'; // Added useState and useRef
import clsx from 'clsx';
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
    ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Image from '@/elements/Image/Image.component';
import Button from '@/elements/Button/Button.component';
import Popover from '@/elements/Popover/Popover.component'; // Import Popover
import { GlobalSearch } from '@/components/GlobalSearch/GlobalSearch.component'; // Import GlobalSearch
import { IProps } from './GlobalHeader.interface';
import Heading from '@/elements/Heading/Heading.component';
import './GlobalHeader.styles.css';
import DonationBlock from '../DonationBlock/DonationBlock.component';
import { ImageCarouselBlock } from '../ImageCarouselBlock/ImageCarouselBlock.component';
import MobileHeader from '../MobileHeader/MobileHeader.component';
import { IDonationWidgetProps } from '../DonationBlock/DonationBlock.interface';
import Text from '@/elements/Text/Text.component';
import Icon from '@/elements/Icon/Icon.component';
import { HeaderLevelOneLink } from './HeaderComponents.component';

/**
 * Custom hook to calculate and set CSS variable for panel positioning
 * Dynamically updates --panel-grid-top based on header position
 */
const useDesktopHeaderTop = () => {
    const triggerRef = React.useRef<HTMLDivElement | null>(null);
    // No longer need to manage state for desktopHeaderTop here if the hook sets the CSS var directly.

    React.useEffect(() => {
        const updateTop = () => {
            if (typeof window === 'undefined' || !triggerRef.current) {
                // Clear the variable if ref is not available or in SSR
                document.documentElement.style.removeProperty('--panel-grid-top');
                return;
            }
            const rect = triggerRef.current.getBoundingClientRect();
            // Ensure rect.bottom is a valid number
            if (typeof rect.bottom === 'number') {
                const newTop = `${rect.bottom - 40}px`;
                document.documentElement.style.setProperty('--panel-grid-top', newTop);
            } else {
                document.documentElement.style.removeProperty('--panel-grid-top');
            }
        };

        updateTop(); // Initial set
        window.addEventListener('resize', updateTop);
        window.addEventListener('scroll', updateTop, { passive: true });

        return () => {
            window.removeEventListener('resize', updateTop);
            window.removeEventListener('scroll', updateTop);
            // Clean up the CSS variable when the component unmounts
            document.documentElement.style.removeProperty('--panel-grid-top');
        };
    }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

    return { triggerRef }; // Only return the ref
};

/**
 * GlobalHeader Component
 * 
 * Renders the main site navigation with:
 * - Logo
 * - Multi-level navigation (L1-L4)
 * - Donation button with popover
 * - Search functionality
 * - Mobile/Desktop responsive versions
 */
export const GlobalHeader: React.FC<IProps> = (props) => {
    const {
        className,
        logo,
        navItems,
        donateButton = true,
        donationBlock,
        donationStories,
        waysToHelp,
        searchButton = true,
        autoCompleteButtonText,
        autoCompletePlaceholder,
        autoCompleteMobilePlaceholder,
        autoCompleteApiUrl,
        autoCompleteRedirectUrl,
        ...blockWrapperProps
    } = props;

    const [isSearchOpen, setIsSearchOpen] = useState(false); // State for GlobalSearch
    const searchButtonRef = useRef<HTMLButtonElement>(null); // Ref for the search button

    const { triggerRef: headerWrapperRef } = useDesktopHeaderTop();

    return (
        // Main header wrapper with ref for positioning calculations
        <div className={clsx('globalHeader', className)} ref={headerWrapperRef}>
            {/* DESKTOP VERSION - Hidden on mobile/tablet */}
            <BlockWrapper
                layout="hero"
                {...blockWrapperProps}
                className="hidden lg:block"
            >
                <ContentWrapper
                    // className="!p-4"
                    className="!p-0 !py-4 !max-w-full"
            >
                    <ContentColumn className="globalHeader__container !p-0 !m-0 !px-8">
                        {/* LOGO SECTION */}
                        {logo && (
                            <div className="globalHeader__logo">
                                <a href={logo.href || '/'}>
                                    {/* Ensure Image props adhere to interface */}
                                    <Image {...logo} />
                                </a>
                            </div>
                        )}
                        {/* MAIN NAVIGATION - Multi-level menu system */}
                        {navItems && navItems.length > 0 && (
                            <nav className="globalHeader__nav" aria-label="Main Navigation">
                                <ul className="globalHeader__navList">
                                    {navItems.map((item, index) => {
                                        // Validate each nav item exists before processing
                                        if (!item) return null;
                                        return (
                                            <li
                                                key={`global-header-navlist-item-${index}`}
                                                className="globalHeader__navItem"
                                            >
                                                {/* L1: Simple link navigation item */}
                                                {item.type === 'link' && item.link ? (
                                                    <a
                                                        href={item.link.href}
                                                        className="globalHeader__navLink"
                                                        title={item.link.title || ''}
                                                        aria-label={item.link.ariaLabel || ''}
                                                        target={item.link.target || '_self'}
                                                    >
                                                        {item.link.text || item.link.title}
                                                    </a>
                                                ) : /* L1: Complex popover navigation with multi-level links */
                                                item.type === 'dropdown' && item.linkLists && item.linkLists.some(list => list && (list.heading?.text || (list.subLinks && list.subLinks.length > 0))) ? (
                                                    <Popover>
                                                        {/* L1: Main navigation button with chevron */}
                                                        <Popover.Trigger asChild>
                                                            {/* Removed ref, onClick, aria-expanded, aria-controls */}
                                                            <Button
                                                                // Removed ref, onClick, aria-expanded, aria-controls
                                                                as="button"
                                                                variant="tertiary"
                                                                icon="chevron-down"
                                                                color="black"
                                                                iconPosition="after"
                                                                // Add data-state styling for icon rotation
                                                                className="globalHeader__navLink--chevron"
                                                            >
                                                                {(item as any).heading}
                                                            </Button>
                                                        </Popover.Trigger>
                                                        <Popover.Content
                                                            // Apply panel classes and conditional promo class
                                                            className={clsx(
                                                                'globalHeader__navPanel', // Base panel styles
                                                                // Conditional layout styles when promo exists
                                                                item.promo &&
                                                                'globalHeader__panelGrid--withPromo',
                                                            )}
                                                            // Position below the trigger
                                                            side="bottom"
                                                            // Center under the trigger button
                                                            align="center"
                                                            // Adjust offset from trigger
                                                            sideOffset={8} // Match promo panel offset
                                                        // Removed manual keyboard handling
                                                        >
                                                            {/* POPOVER CONTENT: Multi-level navigation links */}
                                                            {item.linkLists && (
                                                                <div className="globalHeader__linkLists--wrapper">
                                                                    {/* Optional L1 overview link at top of popover */}
                                                                    {item?.link?.href && item.link?.text && (
                                                                        <HeaderLevelOneLink {...item.link} />
                                                                    )}

                                                                    <div className="globalHeader__linkLists--container">
                                                                        <div className={clsx("globalHeader__linkLists", {
                                                                            'grid-cols-1': item.linkLists.length === 1,
                                                                            'grid-cols-2': item.linkLists.length === 2,
                                                                            'grid-cols-3': item.linkLists.length === 3,
                                                                            'xl:grid-cols-4': item.linkLists.length >= 4,
                                                                        })}>
                                                                            {/* L2-L4: Column-based navigation structure */}
                                                                            {item?.linkLists?.map((list, colIdx) => {
                                                                                if (!list) return null;
                                                                                return (
                                                                                    <ul
                                                                                        key={`global-header-linklists-list-col-${colIdx}`}
                                                                                        className="globalHeader__linkList"
                                                                                    >
                                                                                        {/* L2: Column heading (can be link or text) */}
                                                                                        {list.heading?.text && (
                                                                                            <li className="globalHeader__linkItem">
                                                                                                {list.heading?.href ? (
                                                                                                    <a
                                                                                                        className="globalHeader__link globalHeader__link--heading"
                                                                                                        href={list.heading.href}
                                                                                                        title={list.heading.title || ''}
                                                                                                        aria-label={
                                                                                                            list.heading.ariaLabel || ''
                                                                                                        }
                                                                                                        target={
                                                                                                            list.heading.target || '_self'
                                                                                                        }
                                                                                                    >
                                                                                                        {list.heading.text}
                                                                                                    </a>
                                                                                                    ) : (
                                                                                                    <span className="globalHeader__link globalHeader__link--heading">
                                                                                                        {list.heading.text}
                                                                                                    </span>
                                                                                                )}
                                                                                            </li>
                                                                                        )}
                                                                                        {/* L3: Primary navigation links under each column */}
                                                                                        {list.subLinks
                                                                                            ? list.subLinks.map((link, idx) => {
                                                                                                // Validate each L3 link exists
                                                                                                if (!link) return null;
                                                                                                return (
                                                                                                    <li
                                                                                                        key={`global-header-sublink-${idx}`}
                                                                                                        className="globalHeader__linkItem"
                                                                                                    >
                                                                                                        <a
                                                                                                            href={link.href}
                                                                                                            className="globalHeader__link"
                                                                                                            title={link.title || ''}
                                                                                                            aria-label={link.ariaLabel || ''}
                                                                                                            target={link.target || '_self'}
                                                                                                        >
                                                                                                            {link.text || link.title}
                                                                                                        </a>
                                                                                                        {/* L4: Deepest navigation level (nested under L3) */}
                                                                                                        {link?.subLinks && (
                                                                                                            <ul className="globalHeader__subLinkList">
                                                                                                                {link.subLinks.map((subLink, subIdx) => {
                                                                                                                    // Validate each L4 sublink exists
                                                                                                                    if (!subLink) return null;
                                                                                                                    return (
                                                                                                                    <li
                                                                                                                        key={`global-header-sublink-level4-${subIdx}`}
                                                                                                                        className="globalHeader__subLinkItem"
                                                                                                                    >
                                                                                                                        <a
                                                                                                                            href={subLink.href}
                                                                                                                            className="globalHeader__subLink"
                                                                                                                            title={subLink.title || ''}
                                                                                                                            aria-label={subLink.ariaLabel || ''}
                                                                                                                            target={subLink.target || '_self'}
                                                                                                                        >
                                                                                                                            {subLink.text || subLink.title}
                                                                                                                        </a>
                                                                                                                    </li>
                                                                                                                    );
                                                                                                                })}
                                                                                                            </ul>
                                                                                                        )}
                                                                                                    </li>
                                                                                                );
                                                                                            })
                                                                                            : null
                                                                                        }
                                                                                    </ul>
                                                                                );
                                                                            })}
                                                                        </div>

                                                                    </div>
                                                                </div>
                                                            )}
                                                            {/* PROMO SECTION: Optional promotional content with image/CTA */}
                                                            {item.promo && (
                                                                <div
                                                                    className={clsx(
                                                                        'globalHeader__promoArea',
                                                                        className,
                                                                    )}
                                                                >
                                                                    <div className="globalHeader__promoImageWrapper">
                                                                        {item.promo.image && (
                                                                            <Image
                                                                                defaultSrc={item.promo.image.defaultSrc}
                                                                                srcSet={item.promo.image.srcSet}
                                                                                alternateText={
                                                                                    item.promo.image.alternateText || ''
                                                                                }
                                                                                className="globalHeader__promoImage"
                                                                            />
                                                                        )}
                                                                        <div className="globalHeader__promoOverlay">
                                                                            {item.promo.heading && (
                                                                                <Heading
                                                                                    level={3}
                                                                                    html={item.promo.heading}
                                                                                    className="globalHeader__promoHeading"
                                                                                />
                                                                            )}
                                                                            {item.promo.button && (
                                                                                <Button
                                                                                    {...item.promo.button}
                                                                                    className="globalHeader__promoButton"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Popover.Content>
                                                    </Popover>
                                                ) : (
                                                    item.type === 'dropdown' &&
                                                        <span className="globalHeader__navLink">
                                                            {item.heading}
                                                        </span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </nav>
                        )}
                        {/* ACTION BUTTONS SECTION: Donate & Search */}
                        <div className="globalHeader__actions">
                            {donateButton && (donationBlock || waysToHelp || donationStories) && (
                                <Popover>
                                    <Popover.Trigger
                                        asChild
                                        className="globalHeader__donate--trigger"
                                    >
                                        <Button
                                            as="button"
                                            children="Donate"
                                            variant="primary"
                                            icon="chevron-down"
                                            iconPosition="after"
                                        />
                                    </Popover.Trigger>
                                    <Popover.Content
                                        className="globalHeader__donate globalHeader__navPanel globalHeader__panelGrid--withPromo"
                                        side="bottom"
                                        sideOffset={8}
                                    >
                                        <div className="globalHeader__donate--content">
                                            <div className="globalHeader__donate--carousel">
                                                {
                                                    waysToHelp?.items && waysToHelp?.items?.length > 0 ? (
                                                        <ImageCarouselBlock
                                                            paddingTop={'medium'}
                                                            paddingBottom={'medium'}
                                                            slidesPerViewDesktop={3}
                                                            slidesPerViewTablet={3}
                                                            slidesPerViewMobile={'auto'}
                                                            spaceBetween={20}
                                                            loop={true}
                                                            autoplay={false}
                                                            autoplayDelay={3000}
                                                            {...waysToHelp}
                                                        />
                                                    ) : null
                                                }
                                                <div className="globalHeader__donationStories">
                                                    {donationStories?.title && (
                                                        <Heading
                                                            level={3}
                                                            html={donationStories.title}
                                                            className="globalHeader__donationStories--title"
                                                        />
                                                    )}
                                                    {donationStories?.description && (
                                                        <Text
                                                            html={donationStories.description}
                                                            className="globalHeader__donationStories--description"
                                                        />
                                                    )}
                                                    {donationStories?.items &&
                                                        donationStories?.items?.length > 0 && (
                                                            <ul className="globalHeader__donationStories--list">
                                                                {donationStories.items.map((item, index) => {
                                                                    if (!item) return null;
                                                                    return (
                                                                        <li
                                                                            key={`donation-story-${index}`}
                                                                            className="globalHeader__donationStoryItem"
                                                                        >
                                                                            <a {...item}>
                                                                                {item.children && item.children}{' '}
                                                                                <Icon icon="arrow-right" />
                                                                            </a>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        )}
                                                </div>
                                            </div>
                                            {
                                                donationBlock && donationBlock.formId && (
                                                    <DonationBlock
                                                        donateform={donationBlock as IDonationWidgetProps}
                                                        className="globalHeader__donate--form"
                                                        key={`donation-block-${donationBlock?.formId || 'default'}`}
                                                    />
                                                )
                                            }
                                        </div>
                                    </Popover.Content>
                                </Popover>
                            )}
                            {searchButton && (
                                <Button
                                    as="button"
                                    ref={searchButtonRef} // Assign ref to the search button
                                    variant="tertiary"
                                    color="black"
                                    icon="magnifying-glass" // Corrected icon name
                                    onClick={() => setIsSearchOpen(true)} // Open GlobalSearch
                                    aria-label="Open search"
                                />
                            )}
                        </div>
                    </ContentColumn>
                </ContentWrapper>
            </BlockWrapper>

            {/* MOBILE VERSION - Shows on mobile/tablet, hidden on desktop */}
            <BlockWrapper className="globalHeader__mobile-container block lg:hidden">
                <ContentWrapper>
                    <ContentColumn className="globalHeader__mobile-content flex gap-0">
                        {/* Mobile logo */}
                        {logo && (
                            <div className="globalHeader__logo globalHeader__logo--mobile">
                                <a href={logo.href || '/'}>
                                    {/* Ensure Image props adhere to interface */}
                                    <Image {...logo} />
                                </a>
                            </div>
                        )}
                        {donateButton && (donationBlock || waysToHelp || donationStories) && (
                            <Popover>
                                <Popover.Trigger asChild className="ms-auto mr-2">
                                    <Button
                                        as="button"
                                        children="Donate"
                                        variant="primary"
                                        icon="chevron-down"
                                        iconPosition="after"
                                        size="sm"
                                    />
                                </Popover.Trigger>
                                <Popover.Content
                                    className="globalHeader__donate globalHeader__navPanel globalHeader__panelGrid--withPromo"
                                    side="bottom"
                                    sideOffset={8}
                                >
                                    <div className="globalHeader__donate--content">
                                        <DonationBlock
                                            donateform={donationBlock as IDonationWidgetProps}
                                            className="globalHeader__donate--form"
                                        />
                                    </div>
                                </Popover.Content>
                            </Popover>
                        )}
                        {searchButton && (
                            <Button
                                ref={searchButtonRef} // Assign ref to the search button
                                variant="tertiary"
                                color="black"
                                icon="magnifying-glass" // Corrected icon name
                                onClick={() => setIsSearchOpen(true)} // Open GlobalSearch
                                aria-label="Open search"
                            />
                        )}

                        <MobileHeader navItems={navItems} />

                    </ContentColumn>
                </ContentWrapper>
            </BlockWrapper>

            {/* GLOBAL SEARCH MODAL: Conditionally rendered search overlay */}
            {searchButton && (
                <GlobalSearch
                    open={isSearchOpen}
                    onOpenChange={(openState) => {
                        setIsSearchOpen(openState);
                    }}
                    autoCompletePlaceholder={autoCompletePlaceholder}
                    autoCompleteMobilePlaceholder={autoCompleteMobilePlaceholder}
                    autoCompleteApiUrl={autoCompleteApiUrl}
                    autoCompleteRedirectUrl={autoCompleteRedirectUrl}
                    autoCompleteButtonText={autoCompleteButtonText}
                />
            )}
        </div>
    );
};

export default GlobalHeader;
